import DOMPurify from 'dompurify';
import { AiChatItem, AiChatMessageItem, AiChatMessageResponse, AiModelItem } from './ai-chat.types';

const aiChatPromptTags = [
    'a',
    'b',
    'blockquote',
    'br',
    'code',
    'del',
    'em',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'hr',
    'i',
    'img',
    'li',
    'ol',
    'p',
    'pre',
    's',
    'strong',
    'sub',
    'sup',
    'table',
    'tbody',
    'td',
    'th',
    'thead',
    'tr',
    'u',
    'ul',
];

/** Sanitizes user-authored prompt HTML while retaining semantic formatting and supported image sources. */
export function sanitizeAiChatPromptHtml(html: string): string {
    const sanitizedHtml = DOMPurify.sanitize(html, {
        ADD_DATA_URI_TAGS: ['img'],
        ALLOWED_ATTR: ['alt', 'href', 'src'],
        ALLOWED_TAGS: aiChatPromptTags,
    });
    const container = document.createElement('div');
    container.innerHTML = sanitizedHtml;

    container.querySelectorAll('img').forEach((image) => {
        const source = image.getAttribute('src') ?? '';

        if (!isAllowedAiChatImageSource(source)) {
            image.remove();
        }
    });

    return container.innerHTML;
}

/** Removes editor-created empty paragraphs at the end of a prompt before it is sent or retained. */
export function trimAiChatPromptTrailingParagraphs(html: string): string {
    const container = document.createElement('div');
    container.innerHTML = sanitizeAiChatPromptHtml(html);

    while (isEmptyAiChatPromptParagraph(container.lastElementChild)) {
        container.lastElementChild?.remove();
    }

    return container.innerHTML;
}

function isEmptyAiChatPromptParagraph(element: Element | null): boolean {
    return element?.tagName === 'P' && !element.textContent?.trim() && !element.querySelector('img, video, iframe, table');
}

function isAllowedAiChatImageSource(source: string): boolean {
    return /^(?:https?:\/\/|data:image\/(?:avif|gif|jpe?g|png|webp);base64,)/i.test(source);
}

/** Decodes a Base64 UTF-8 value, returning the original value if it is not valid Base64. */
export function decodeAiChatBase64(value: string) {
    if (!value) return '';

    try {
        const bytes = Uint8Array.from(atob(value), (character) => character.charCodeAt(0));
        return new TextDecoder().decode(bytes);
    } catch {
        return value;
    }
}

/** Decodes a stored prompt only when its decoded value is HTML. */
export function decodeAiChatStoredHtml(value: string) {
    if (!value) return '';

    const normalized = value.replace(/\s/g, '');
    if (!normalized || normalized.length % 4 !== 0 || !/^[A-Za-z0-9+/]+={0,2}$/.test(normalized)) return value;

    const decoded = decodeAiChatBase64(normalized);
    return /<\/?[a-z][\s\S]*>/i.test(decoded) ? decoded : value;
}

/** Maps a stored message to the decoded client-side representation used by the conversation UI. */
export function mapAiChatMessage(message: AiChatMessageResponse): AiChatMessageItem {
    return {
        ...message,
        prompt: decodeAiChatStoredHtml(message.prompt),
        response: decodeAiChatBase64(message.response_base64),
    };
}

/** Returns one provider name per provider key from the loaded model rows. */
export function getAiChatProviderNames(models: AiModelItem[]) {
    return models.reduce<Record<string, string>>((names, model) => {
        names[model.ai_provider_keyno] ??= model.ai_provider_name;
        return names;
    }, {});
}

/** Sorts valid modified dates newest-first while retaining source order for equal or invalid values. */
export function sortAiChatsByModifiedDate(chats: AiChatItem[]) {
    const datedChats = chats
        .map((chat, index) => ({ chat, index, modifiedAt: parseAiChatModifiedDate(chat.mod_datetime) }))
        .filter((item) => Number.isFinite(item.modifiedAt))
        .sort((left, right) => right.modifiedAt - left.modifiedAt || left.index - right.index)
        .map(({ chat }) => chat);

    const undatedChats = chats.filter((chat) => !Number.isFinite(parseAiChatModifiedDate(chat.mod_datetime)));
    return [...datedChats, ...undatedChats];
}

/** Converts an unknown polling failure into a user-displayable error message. */
export function getAiChatPollErrorMessage(error: unknown) {
    if (error instanceof Error && error.message) return error.message;
    if (typeof error === 'string' && error) return error;
    if (error && typeof error === 'object') {
        const dataTaskError = error as { errormessage?: string; message?: string };
        if (dataTaskError.errormessage) return dataTaskError.errormessage;
        if (dataTaskError.message) return dataTaskError.message;
    }

    return 'The AI response status could not be retrieved.';
}

function parseAiChatModifiedDate(value: string) {
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;

    const match = value.match(/^(\d{1,2})[.-](\d{1,2})[.-](\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
    if (!match) return Number.NaN;

    const [, day, month, year, hours = '0', minutes = '0', seconds = '0'] = match;
    const date = new Date(Number(year), Number(month) - 1, Number(day), Number(hours), Number(minutes), Number(seconds));
    return date.getFullYear() === Number(year) && date.getMonth() === Number(month) - 1 && date.getDate() === Number(day) ? date.getTime() : Number.NaN;
}
